// src/components/AuthorityAnalysis.jsx
import React, { useEffect, useState } from 'react'
import DataViewer from './DataViewer'
import { useTranslation } from 'react-i18next'

function AuthorityAnalysis({ staffData, authorityAnalysis, currentStaff = [], settings, onStaffUpdate }) {
    const { t, locale } = useTranslation()
    const [activeTab, setActiveTab] = useState('summary')
    const [expandedSections, setExpandedSections] = useState({
        issues: false,
        teachers: false,
        headOfYear: false,
        headOfKeyStage: false, // NEW
        fullAccess: false,
        blank: false,
        unknown: false,
        staffChanges: false,
        missingPasswords: false,
    })

    // Add state for staff comparison
    const [staffComparison, setStaffComparison] = useState({
        added: [],
        removed: [],
        unchanged: [],
    })

    // Add state for local staff data modifications
    const [localStaffData, setLocalStaffData] = useState(staffData)
    const [localAuthorityAnalysis, setLocalAuthorityAnalysis] = useState(authorityAnalysis)

    // Update local state when props change
    useEffect(() => {
        setLocalStaffData(staffData)
        setLocalAuthorityAnalysis(authorityAnalysis)
    }, [staffData, authorityAnalysis])

    // Compare staff data when dependencies change
    useEffect(() => {
        if (localStaffData && localStaffData.length > 0 && currentStaff.length > 0) {
            compareStaff()
        }
    }, [localStaffData, currentStaff])

    if (!localStaffData || localStaffData.length === 0 || !localAuthorityAnalysis) {
        return null
    }

    // Function to mark unknown staff as "No Class assigned authority" teachers
    const markAsNoClassTeacher = (staffMember) => {
        // Update the staff member
        const updatedStaff = {
            ...staffMember,
            scopeField: 'none',
            scope: [],
            markedAsTeacher: true,
        }

        // Update local staff data
        const updatedStaffData = localStaffData.map((staff) => (staff === staffMember ? updatedStaff : staff))

        // Update categorized data
        const updatedCategorized = { ...localAuthorityAnalysis.categorized }

        // Remove from unknown
        updatedCategorized.unknown = updatedCategorized.unknown.filter((staff) => staff !== staffMember)

        // Add to blank (no class assigned) if not already there
        if (!updatedCategorized.blank) {
            updatedCategorized.blank = []
        }
        updatedCategorized.blank.push(updatedStaff)

        // Update summary
        const updatedSummary = {
            ...localAuthorityAnalysis.summary,
            unknown: updatedCategorized.unknown.length,
            blank: updatedCategorized.blank.length,
        }

        // Update local authority analysis
        const updatedAnalysis = {
            ...localAuthorityAnalysis,
            categorized: updatedCategorized,
            summary: updatedSummary,
        }

        setLocalStaffData(updatedStaffData)
        setLocalAuthorityAnalysis(updatedAnalysis)

        // Call parent update function if provided
        if (onStaffUpdate) {
            onStaffUpdate(updatedStaffData, updatedAnalysis)
        }
    }

    // Function to mark multiple unknown staff as teachers
    const markAllUnknownAsTeachers = () => {
        const unknownStaff = localAuthorityAnalysis.categorized.unknown || []

        if (unknownStaff.length === 0) return

        // Update all unknown staff
        const updatedUnknownStaff = unknownStaff.map((staff) => ({
            ...staff,
            scopeField: 'none',
            scope: [],
            markedAsTeacher: true,
        }))

        // Update local staff data
        const updatedStaffData = localStaffData.map((staff) => {
            const unknownMatch = unknownStaff.find((unknown) => unknown === staff)
            if (unknownMatch) {
                return {
                    ...staff,
                    scopeField: 'none',
                    scope: [],
                    markedAsTeacher: true,
                }
            }
            return staff
        })

        // Update categorized data
        const updatedCategorized = { ...localAuthorityAnalysis.categorized }

        // Clear unknown
        updatedCategorized.unknown = []

        // Add all to blank
        if (!updatedCategorized.blank) {
            updatedCategorized.blank = []
        }
        updatedCategorized.blank = [...updatedCategorized.blank, ...updatedUnknownStaff]

        // Update summary
        const updatedSummary = {
            ...localAuthorityAnalysis.summary,
            unknown: 0,
            blank: updatedCategorized.blank.length,
        }

        // Update local authority analysis
        const updatedAnalysis = {
            ...localAuthorityAnalysis,
            categorized: updatedCategorized,
            summary: updatedSummary,
        }

        setLocalStaffData(updatedStaffData)
        setLocalAuthorityAnalysis(updatedAnalysis)

        // Call parent update function if provided
        if (onStaffUpdate) {
            onStaffUpdate(updatedStaffData, updatedAnalysis)
        }
    }

    const findEmailColumn = (staffSample) => {
        if (!staffSample) return null

        const possibleEmailColumns = ['email', 'mail', 'username', 'address']

        for (const column of possibleEmailColumns) {
            const foundColumn = Object.keys(staffSample).find((key) => key.toLowerCase().includes(column))

            if (foundColumn) {
                return foundColumn
            }
        }

        return null
    }

    // Helper to find name field in staff data
    const findNameField = (staff, type) => {
        const possibleFields = type === 'first' ? ['first_name', 'firstname', 'first name'] : ['last_name', 'lastname', 'last name']

        for (const field of possibleFields) {
            const foundField = Object.keys(staff).find((key) => key.toLowerCase() === field.toLowerCase())

            if (foundField && staff[foundField]) {
                return staff[foundField]
            }
        }

        // If no specific first/last name field found, try to find a full name field
        if (type === 'first') {
            const fullNameFields = ['name', 'full_name', 'fullname', 'full name']

            for (const field of fullNameFields) {
                const foundField = Object.keys(staff).find((key) => key.toLowerCase() === field.toLowerCase())

                if (foundField && staff[foundField]) {
                    const nameParts = staff[foundField].split(' ')
                    return nameParts[0] || ''
                }
            }
        }

        return ''
    }

    const compareStaff = () => {
        // Extract email column from staff data
        const emailColumn = findEmailColumn(localStaffData[0])

        if (!emailColumn) return

        const currentStaffMap = new Map(currentStaff.map((s) => [s.email.toLowerCase(), s]))

        const uploadStaffEmails = localStaffData
            .filter((s) => s[emailColumn])
            .map((s) => ({
                email: s[emailColumn].toLowerCase(),
                firstName: findNameField(s, 'first'),
                lastName: findNameField(s, 'last'),
                original: s,
            }))

        const uploadStaffMap = new Map(uploadStaffEmails.map((s) => [s.email, s]))

        const added = uploadStaffEmails.filter((s) => !currentStaffMap.has(s.email))
        const removed = currentStaff.filter((s) => !uploadStaffMap.has(s.email.toLowerCase()))
        const unchanged = uploadStaffEmails.filter((s) => currentStaffMap.has(s.email))

        setStaffComparison({ added, removed, unchanged })
    }

    const { categorized, summary, issues, authorityColumn } = localAuthorityAnalysis

    const toggleSection = (section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }))
    }

    const handleCategoryClick = (category) => {
        setActiveTab('details')
        // Set all sections to false first, then set the current category to true
        setExpandedSections({
            issues: false,
            teachers: false,
            headOfYear: false,
            headOfKeyStage: false, // NEW
            fullAccess: false,
            blank: false,
            unknown: false,
            staffChanges: false,
            missingPasswords: false,
            [category]: true,
        })
    }

    const renderStaffList = (staffList, highlightIssue = false, showMarkAsTeacherButton = false) => {
        if (!staffList || staffList.length === 0) {
            return <p className="text-mudPurple font-medium italic">No staff members in this category</p>
        }

        const showPasswords = settings?.useSheetPassword && localAuthorityAnalysis.passwordColumn

        return (
            <div className="overflow-x-auto">
                {showMarkAsTeacherButton && staffList.length > 0 && (
                    <div className="mb-4 p-3 bg-containerGray border border-lightBG rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="text-sm font-medium text-darkPurple">{t('markAsTeachers')}</h4>
                                <p className="text-xs text-darkPurple">{t('markUnknownFormatAsTeachers')}</p>
                            </div>
                            <button onClick={markAllUnknownAsTeachers} className="px-3 py-1.5 bg-deepPurple text-white rounded-md hover:bg-darkPurple text-sm">
                                {t('markAllAsTeachers')}
                            </button>
                        </div>
                    </div>
                )}

                <table className="min-w-full divide-y divide-lightBG">
                    <thead className="bg-lightBG">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('id')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('name')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('authority')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('scopeField')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('scope')}</th>
                            {showPasswords && <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('password')}</th>}
                            {highlightIssue && <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('issue')}</th>}
                            {showMarkAsTeacherButton && <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('actions')}</th>}
                        </tr>
                    </thead>

                    <tbody className="bg-containerGray divide-y divide-lightBG">
                        {staffList.map((staff, index) => {
                            const staffId = staff.ID || staff.Id || staff.id || ''
                            const staffName = getStaffName(staff)
                            const authority = staff[localAuthorityAnalysis.authorityColumn] || ''
                            const password = showPasswords ? staff[localAuthorityAnalysis.passwordColumn] || '' : ''
                            const scopeField = staff.scopeField || 'unknown'
                            const scope = staff.scope || []

                            return (
                                <tr key={index} className={highlightIssue ? 'bg-containerGray' : 'hover:bg-containerGray'}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-darkPurple">{staffId}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-darkPurple">{staffName}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-darkPurple">{authority}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                                        <span className={`px-2 py-1 text-xs rounded-full bg-white text-darkPurple`}>
                                            {scopeField === 'admin'
                                                ? t('admin')
                                                : scopeField === 'keystage'
                                                ? t('keyStage')
                                                : scopeField === 'year'
                                                ? t('year')
                                                : scopeField === 'class'
                                                ? t('class')
                                                : scopeField === 'none'
                                                ? t('none')
                                                : t('unknown')}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-darkPurple">
                                        {scope.length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {scope.map((item, idx) => (
                                                    <span key={idx} className="px-2 py-1 rounded text-xs">
                                                        {item}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-darkPurple italic">{staff.markedAsTeacher ? t('noClassAssigned') : t('noScopeDefined')}</span>
                                        )}
                                    </td>

                                    {showPasswords && (
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-darkPurple">
                                            {password ? (
                                                <span className="font-mono px-2 py-1 rounded text-xs">{password}</span>
                                            ) : (
                                                <span className="text-[#FC4C59] text-xs">{t('notSet')}</span>
                                            )}
                                        </td>
                                    )}

                                    {highlightIssue && <td className="px-4 py-2 whitespace-nowrap text-sm text-[#FC4C59]">{staff.authorityIssue}</td>}

                                    {showMarkAsTeacherButton && (
                                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                                            <button onClick={() => markAsNoClassTeacher(staff)} className="px-2 py-1 bg-deepPurple text-white rounded font-medium text-sm">
                                                {t('markAsTeacher')}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    const renderStaffChanges = () => {
        const totalChanges = staffComparison.added.length + staffComparison.removed.length

        return (
            <div className="mt-6">
                <div
                    className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#ECEBF8] px-3 rounded-tr-[12px] transition ${
                        expandedSections.staffChanges ? 'rounded-br-none' : 'rounded-br-[12px]'
                    } border-l-[5px] border-deepPurple`}
                    onClick={() => toggleSection('staffChanges')}
                >
                    <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                        {t('Staff Changes')} ({totalChanges})
                    </h4>
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                        <svg
                            className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.staffChanges ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>

                {expandedSections.staffChanges && (
                    <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-[#ECEBF8] p-3 rounded-lg">
                                <h5 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('New Staff')}</h5>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{staffComparison.added.length}</p>
                            </div>

                            <div className="bg-[#ECEBF8] p-3 rounded-lg">
                                <h5 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Removed Staff')}</h5>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{staffComparison.removed.length}</p>
                            </div>
                        </div>

                        {staffComparison.added.length > 0 && (
                            <div className="mt-4">
                                <h5 className="text-md font-medium text-darkPurple mb-2">{t('New Staff to be Added')}:</h5>
                                <div className="bg-white rounded-lg border border-[#ECEBF8] overflow-hidden">
                                    <table className="min-w-full divide-y divide-[#ECEBF8]">
                                        <thead className="bg-containerGray">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Name')}</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Email')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-[#ECEBF8]">
                                            {staffComparison.added.map((staff, index) => (
                                                <tr key={index} className="hover:bg-green-50">
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-darkPurple">
                                                        {staff.firstName} {staff.lastName}
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{staff.email}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {staffComparison.removed.length > 0 && (
                            <div className="mt-4">
                                <h5 className="text-md font-medium text-red-800 mb-2">{t('Staff to be Removed')}:</h5>
                                <div className="bg-white rounded-lg border border-[#ECEBF8] overflow-hidden">
                                    <table className="min-w-full divide-y divide-[#ECEBF8]">
                                        <thead className="bg-containerGray">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Name')}</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Email')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-[#ECEBF8]">
                                            {staffComparison.removed.map((staff, index) => (
                                                <tr key={index} className="hover:bg-red-50">
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-darkPurple">
                                                        {staff.first_name} {staff.last_name}
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{staff.email}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }
    return (
        <div className="bg-[#F8F8FB] rounded-[16px] shadow p-10 ">
            <div className="flex gap-8 border-b border-[#BEBECE] mb-4">
                <button
                    onClick={() => setActiveTab('summary')}
                    className={`px-4 py-2 font-bold text-[20px] leading-[25px] -mb-[2.5px] transition-all duration-300 ease-in-out
            ${activeTab === 'summary' ? 'border-deepPurple border-b-[5px] text-darkPurple' : 'border-b-[5px] border-transparent text-[#9392AD] hover:text-darkPurple'}
        `}
                >
                    {t('Summary')}
                </button>
                <button
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-2 font-bold text-[20px] leading-[25px] -mb-[2.5px] transition-all duration-300 ease-in-out
            ${activeTab === 'details' ? 'border-deepPurple border-b-[5px] text-darkPurple' : 'border-b-[5px] border-transparent text-[#9392AD] hover:text-darkPurple'}
        `}
                >
                    {t('Details')}
                </button>
                <button
                    onClick={() => setActiveTab('preview')}
                    className={`px-4 py-2 font-bold text-[20px] leading-[25px] -mb-[2.5px] transition-all duration-300 ease-in-out
            ${activeTab === 'preview' ? 'border-deepPurple border-b-[5px] text-darkPurple' : 'border-b-[5px] border-transparent text-[#9392AD] hover:text-darkPurple'}
        `}
                >
                    {t('Data preview')}
                </button>
            </div>

            {activeTab === 'summary' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('teachers')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('teachers')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.teachers}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                {((summary.teachers / summary.total) * 100).toFixed(1)}% {t('ofStaff')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('headOfKeyStage')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('headOfKeyStage')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.headOfKeyStage || 0}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                {(((summary.headOfKeyStage || 0) / summary.total) * 100).toFixed(1)}% {t('ofStaff')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('headOfYear')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('headOfYear')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.headOfYear}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                {((summary.headOfYear / summary.total) * 100).toFixed(1)}% {t('ofStaff')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('fullAccess')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('fullAccess')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.fullAccess}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                {((summary.fullAccess / summary.total) * 100).toFixed(1)}% {t('ofStaff')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('blank')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('noClassAssignedAuthority')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.blank}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                {((summary.blank / summary.total) * 100).toFixed(1)}% {t('ofStaff')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('unknown')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('unknownFormat')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.unknown}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                {((summary.unknown / summary.total) * 100).toFixed(1)}% {t('ofStaff')}
                            </p>
                        </div>

                        {localAuthorityAnalysis.passwordColumn && (
                            <div
                                className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors"
                                onClick={() => handleCategoryClick('missingPasswords')}
                            >
                                <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('missingPasswords')}</h4>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.missingPasswords}</p>
                                <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                    {((summary.missingPasswords / summary.total) * 100).toFixed(1)}% {t('ofStaff')}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* {(currentStaff.length > 0 || true) && renderStaffChanges()} */}

                    {issues.length > 0 && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.issues ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('issues')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Authority Issues')} ({issues.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.issues ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.issues && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-lg p-4 bg-white">
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-darkPurple">
                                        {issues.map((issue, index) => (
                                            <li key={index}>{issue.message}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'details' && (
                <div className="space-y-6">
                    {/* Issues Section */}
                    {(categorized.blank.length > 0 || categorized.unknown.length > 0) && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.blank ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('blank')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">No Class Assigned Authority ({categorized.blank.length})</h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition  ${expandedSections.blank ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.blank && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStaffList(categorized.blank, false)}</div>
                            )}
                        </div>
                    )}

                    {/* Unknown Format Section */}
                    {categorized.unknown.length > 0 && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.unknown ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('unknown')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">Unknown Authority Format ({categorized.unknown.length})</h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition  ${expandedSections.unknown ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.unknown && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStaffList(categorized.unknown, true, true)}</div>
                            )}
                        </div>
                    )}

                    {/* Full Access Section */}
                    <div className="mt-6">
                        <div
                            className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#ECEBF8] px-3 rounded-tr-[12px] transition ${
                                expandedSections.fullAccess ? 'rounded-br-none' : 'rounded-br-[12px]'
                            } border-l-[5px] border-deepPurple`}
                            onClick={() => toggleSection('fullAccess')}
                        >
                            <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">Full Access ({categorized.fullAccess.length})</h4>
                            <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                <svg
                                    className={`h-5 w-5 text-[#BEBECE] transform transition  ${expandedSections.fullAccess ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {expandedSections.fullAccess && (
                            <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStaffList(categorized.fullAccess)}</div>
                        )}
                    </div>

                    {/* NEW: Head of Key Stage Section */}
                    {categorized.headOfKeyStage && categorized.headOfKeyStage.length > 0 && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.headOfKeyStage ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('headOfKeyStage')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">Head of Key Stage ({categorized.headOfKeyStage.length})</h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition  ${expandedSections.headOfKeyStage ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.headOfKeyStage && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStaffList(categorized.headOfKeyStage)}</div>
                            )}
                        </div>
                    )}

                    {/* Head of Year Section */}
                    <div className="mt-6">
                        <div
                            className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#ECEBF8] px-3 rounded-tr-[12px] transition ${
                                expandedSections.headOfYear ? 'rounded-br-none' : 'rounded-br-[12px]'
                            } border-l-[5px] border-deepPurple`}
                            onClick={() => toggleSection('headOfYear')}
                        >
                            <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">Head of Year ({categorized.headOfYear.length})</h4>
                            <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                <svg
                                    className={`h-5 w-5 text-[#BEBECE] transform transition  ${expandedSections.headOfYear ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {expandedSections.headOfYear && (
                            <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStaffList(categorized.headOfYear)}</div>
                        )}
                    </div>

                    {/* Teachers Section */}
                    <div className="mt-6">
                        <div
                            className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#B7E6C380] px-3 rounded-tr-[12px] transition ${
                                expandedSections.teachers ? 'rounded-br-none' : 'rounded-br-[12px]'
                            } border-l-[5px] border-[#2F8946]`}
                            onClick={() => toggleSection('teachers')}
                        >
                            <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">Teachers ({categorized.teachers.length})</h4>
                            <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                <svg
                                    className={`h-5 w-5 text-[#BEBECE] transform transition  ${expandedSections.teachers ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {expandedSections.teachers && (
                            <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStaffList(categorized.teachers)}</div>
                        )}
                    </div>

                    {/* Missing Passwords Section */}
                    {localAuthorityAnalysis.passwordColumn && categorized.missingPasswords.length > 0 && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.primaryStudents ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('missingPasswords')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">Missing Passwords ({categorized.missingPasswords.length})</h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition  ${expandedSections.missingPasswords ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.missingPasswords && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStaffList(categorized.missingPasswords, false)}</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'preview' && localStaffData.length > 0 && (
                <div className="mt-4">
                    <DataViewer
                        data={localStaffData.map((staff) => ({
                            ...staff,
                            scopeField: staff.scopeField || 'N/A',
                            scope: staff.scope ? staff.scope.join(', ') : 'N/A',
                        }))}
                        title="Staff Data with Scope Information"
                        hideTitle
                    />
                </div>
            )}

            <div className="mt-8">
                <h4 className="text-[16px] leading-5 font-bold text-darkPurple">{t('Authority Types')}</h4>
                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-3 mt-3 ml-2">
                    <li>
                        • <span className="font-medium">{t('Admin')}:</span> {t('Full access to all students and staff data')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Head of Key Stage')}:</span> {t('Manages Primary (Years 1-6 + Early Years) or Secondary (Years 7-13) sections')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Head of Year')}:</span> {t('Manages specific year groups (e.g., "Head of Year 1" or "ALL FS1")')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Teacher')}:</span> {t('Assigned to specific classes (e.g., "FS1A, FS2B" or "Year 3")')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('No Class Assigned')}:</span> {t('Teachers without specific class assignments (can be manually set)')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Unknown Format')}:</span> {t('Authority value does not match expected patterns (can be marked as teachers)')}
                    </li>
                    {localAuthorityAnalysis.passwordColumn && (
                        <li>
                            • <span className="font-medium">{t('Password')}:</span>
                            {settings?.useSheetPassword
                                ? ` ${t('Passwords from Excel sheet will be used for staff accounts')}`
                                : ` ${t('Password column detected but not enabled in settings')}`}
                        </li>
                    )}
                </ul>

                {settings?.useSheetPassword && !localAuthorityAnalysis.passwordColumn && (
                    <div className="mt-2 p-2 bg-yellow-100 border-l-4 border-yellow-400 text-yellow-700 text-sm">
                        <strong>{t('Warning')}:</strong> {t('"Set password from sheet" is enabled but no password column was found in the data.')}
                    </div>
                )}
            </div>
        </div>
    )
}

// Helper function to get staff name (copied from authorityAnalyzer.js for component use)
const getStaffName = (staff) => {
    if (staff['First Name'] && staff['Last Name']) {
        return `${staff['First Name']} ${staff['Last Name']}`
    }

    if (staff['FirstName'] && staff['LastName']) {
        return `${staff['FirstName']} ${staff['LastName']}`
    }

    if (staff['Name']) {
        return staff['Name']
    }

    if (staff['Full Name']) {
        return staff['Full Name']
    }

    for (const key of Object.keys(staff)) {
        if (key.toLowerCase().includes('name') && !key.toLowerCase().includes('user') && staff[key]) {
            return staff[key]
        }
    }

    return 'Unknown Staff'
}

export default AuthorityAnalysis
